// Gestion des serveurs de rebond SSH (page Routeurs).
//
// La plupart des routeurs ne sont joignables que depuis un réseau de gestion :
// la plateforme y passe par un serveur OpenSSH (redirection de ports). Cette
// modale liste les rebonds, les teste, et gère l'épinglage de leur clé SSH.
//
// « Tester la connexion » teste les valeurs SAISIES (POST …/jumphosts/test, rien
// n'est enregistré) : le technicien voit l'empreinte SSH avant d'ajouter, comme
// pour un routeur. Elle repart en `expectedFingerprint` : la plateforme refuse
// d'enregistrer (409) si le serveur en présente une autre entre-temps.

import { useCallback, useEffect, useState } from 'react';
import { X, Network, CheckCircle2, AlertTriangle, Loader2, Plus, Star, Trash2, Pencil } from 'lucide-react';
import { mikrotikCall, testFailureMessage, fmtDateTime } from '../lib/mikrotik.jsx';
import { fmtRelativeFromNow } from '../lib/format.jsx';

const BASE = '/platform/mikrotik/jumphosts';
const labelCls = 'block text-[11px] text-muted-2 mb-1';
const inputCls =
  'w-full bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none focus:border-blue-500';
const btnCls =
  'bg-rule border border-rule-3 rounded-md px-2.5 py-1 text-fg-2 cursor-pointer text-[11px] font-semibold hover:bg-rule-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1';
const thCls = 'px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap';
const tdCls = 'px-3 py-2.5 text-fg-2 border-b border-sidebar text-[12px] align-top';

const jumpFailureMessage = (res) =>
  res?.status === 'hostkey-mismatch'
    ? "La clé SSH présentée par le serveur de rebond n'est plus celle mémorisée. Si le changement est voulu, utiliser « Accepter la nouvelle clé »." +
      (res.error ? ' (' + res.error + ')' : '')
    : testFailureMessage(res);

// Classes complètes (Tailwind ne génère pas les noms construits par concaténation).
const ALERT_TONES = {
  crit: ['bg-crit/10 border-crit/30', 'text-crit'],
  ok: ['bg-ok/10 border-ok/30', 'text-ok'],
};

function Alert({ children, tone = 'crit' }) {
  const [box, icon] = ALERT_TONES[tone] || ALERT_TONES.crit;
  return (
    <div className={`mt-3 ${box} border rounded-xl px-3.5 py-2.5 text-[12px] text-fg-2 flex gap-2`}>
      <AlertTriangle size={14} strokeWidth={1.75} className={`${icon} flex-shrink-0 mt-0.5`} />
      <span>{children}</span>
    </div>
  );
}

function JumpForm({ jump, onClose }) {
  const editing = !!jump;
  const [form, setForm] = useState({
    name: jump?.name || '',
    host: jump?.host || '',
    port: jump?.port || 22,
    username: jump?.username || '',
    password: '',
    isDefault: !!jump?.isDefault,
  });
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (p) => {
    setForm((f) => ({ ...f, ...p }));
    setError(null);
  };
  const portOk = Number(form.port) >= 1 && Number(form.port) <= 65535;
  const canSubmit =
    form.name.trim() && form.host.trim() && form.username.trim() && portOk && (editing || form.password) && !saving;

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setError(null);
    // En modification, ?id= laisse la plateforme reprendre le mot de passe
    // enregistré quand le champ est vide.
    const { ok, status, json } = await mikrotikCall(`${BASE}/test${editing ? '?id=' + jump.id : ''}`, 'POST', {
      name: form.name.trim(),
      host: form.host.trim(),
      port: Number(form.port),
      username: form.username.trim(),
      password: form.password || null,
    });
    setTesting(false);
    if (ok) setTest(json);
    else setError(json?.error || 'Échec du test (erreur ' + status + ').');
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    const body = {
      name: form.name.trim(),
      host: form.host.trim(),
      port: Number(form.port),
      username: form.username.trim(),
      password: form.password || null,
      isDefault: form.isDefault,
      expectedFingerprint: test?.success ? test.hostKeyFingerprint : null,
    };
    const r = editing ? await mikrotikCall(`${BASE}/${jump.id}`, 'PUT', body) : await mikrotikCall(BASE, 'POST', body);
    setSaving(false);
    if (r.ok) return onClose(true);
    if (r.status === 422 || r.status === 409) {
      setTest(r.json && r.json.status ? r.json : null);
      setError(
        r.status === 409
          ? "Le serveur présente maintenant une autre clé SSH que celle vue au test : rien n'a été enregistré."
          : jumpFailureMessage(r.json)
      );
    } else setError(r.json?.error || "Échec de l'enregistrement (erreur " + r.status + ').');
  };

  return (
    <div className="mt-4 border-t border-rule pt-4">
      <h3 className="text-sm font-bold text-fg mb-3">{editing ? `Modifier « ${jump.name} »` : 'Ajouter un serveur de rebond'}</h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Nom</label>
          <input className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })} />
        </div>
        <div className="grid grid-cols-[1fr_90px] gap-3">
          <div>
            <label className={labelCls}>Hôte / adresse IP</label>
            <input
              className={inputCls}
              value={form.host}
              placeholder="10.254.254.20"
              onChange={(e) => {
                set({ host: e.target.value });
                setTest(null);
              }}
            />
          </div>
          <div>
            <label className={labelCls}>Port</label>
            <input
              type="number"
              min="1"
              max="65535"
              className={inputCls}
              value={form.port}
              onChange={(e) => {
                set({ port: e.target.value });
                setTest(null);
              }}
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Utilisateur</label>
          <input
            className={inputCls}
            value={form.username}
            autoComplete="off"
            onChange={(e) => {
              set({ username: e.target.value });
              setTest(null);
            }}
          />
        </div>
        <div>
          <label className={labelCls}>Mot de passe{editing ? ' (vide = inchangé)' : ''}</label>
          <input
            type="password"
            className={inputCls}
            value={form.password}
            autoComplete="new-password"
            placeholder={editing && jump.hasPassword ? '••••••••' : ''}
            onChange={(e) => {
              set({ password: e.target.value });
              setTest(null);
            }}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 mt-3 text-[12px] text-fg-2 cursor-pointer">
        <input type="checkbox" checked={form.isDefault} onChange={(e) => set({ isDefault: e.target.checked })} />
        Rebond par défaut (présélectionné à l'ajout d'un routeur)
      </label>
      <div className="text-[11px] text-muted-2 mt-2 leading-relaxed">
        Le compte du rebond doit autoriser la redirection de ports (<code className="font-mono">AllowTcpForwarding</code>{' '}
        dans sshd). La clé SSH du serveur est mémorisée à l'enregistrement : une clé différente plus tard bloquera l'accès.
      </div>

      <div className="mt-3">
        <button
          type="button"
          className={btnCls}
          onClick={runTest}
          disabled={testing || !form.host.trim() || !form.username.trim() || !portOk || (!editing && !form.password)}
        >
          {testing && <Loader2 size={12} className="animate-spin" />}
          {testing ? 'Test en cours…' : 'Tester la connexion'}
        </button>
      </div>

      {test?.success && (
        <div className="mt-3 bg-ok/10 border border-ok/30 rounded-xl px-3.5 py-3 text-[12px] text-fg-2">
          <div className="flex items-center gap-1.5 font-semibold text-ok mb-1">
            <CheckCircle2 size={14} strokeWidth={2} /> Connexion réussie
          </div>
          <div>
            <span className="text-muted-2">Empreinte SSH : </span>
            <span className="font-mono break-all">
              {test.hostKeyAlgorithm ? test.hostKeyAlgorithm + ' ' : ''}
              {test.hostKeyFingerprint || '—'}
            </span>
          </div>
        </div>
      )}
      {test && !test.success && <Alert>{jumpFailureMessage(test)}</Alert>}
      {error && !(test && !test.success) && <Alert>{error}</Alert>}

      <div className="flex justify-end gap-2.5 mt-4">
        <button
          onClick={() => onClose(false)}
          className="bg-rule border border-rule-3 rounded-lg px-4 py-2 text-muted cursor-pointer text-[13px] font-semibold"
        >
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
        >
          {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter'}
        </button>
      </div>
    </div>
  );
}

export function JumpHostsModal({ canEdit, onClose }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null); // null | 'new' | jump
  const [busy, setBusy] = useState(null); // id en cours d'action
  const [notice, setNotice] = useState(null); // { tone, text }
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    const r = await mikrotikCall(BASE);
    if (r.ok) setRows(r.json || []);
    else {
      setRows([]);
      setLoadError(r.json?.error || 'Chargement impossible (erreur ' + r.status + ').');
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (j, fn) => {
    setBusy(j.id);
    setNotice(null);
    await fn();
    setBusy(null);
    load();
  };

  const test = (j) =>
    act(j, async () => {
      const r = await mikrotikCall(`${BASE}/${j.id}/test`, 'POST', {});
      if (r.ok && r.json?.success) setNotice({ tone: 'ok', text: `« ${j.name} » : connexion réussie.` });
      else
        setNotice({
          tone: 'crit',
          text: `« ${j.name} » : ` + (r.json?.status ? jumpFailureMessage(r.json) : r.json?.error || 'échec du test.'),
          mismatch: r.json?.status === 'hostkey-mismatch' ? j : null,
        });
    });

  const remove = (j) => {
    if (!window.confirm(`Supprimer le serveur de rebond « ${j.name} » ?`)) return;
    act(j, async () => {
      const r = await mikrotikCall(`${BASE}/${j.id}`, 'DELETE');
      if (r.status === 409)
        setNotice({
          tone: 'crit',
          text: `Suppression impossible : ${j.routerCount || 'des'} routeur(s) passent encore par « ${j.name} ». Les repasser en connexion directe ou sur un autre rebond d'abord.`,
        });
      else if (!r.ok) setNotice({ tone: 'crit', text: r.json?.error || 'Échec de la suppression.' });
    });
  };

  const setDefault = (j) =>
    act(j, async () => {
      const r = await mikrotikCall(`${BASE}/${j.id}`, 'PUT', { isDefault: true });
      if (!r.ok) setNotice({ tone: 'crit', text: r.json?.error || 'Échec.' });
    });

  const resetKey = (j) => {
    if (
      !window.confirm(
        `Accepter la nouvelle clé SSH de « ${j.name} » ?\n\nÀ ne faire que si le changement est attendu (serveur réinstallé ou remplacé) : une clé inattendue peut signaler une connexion interceptée.`
      )
    )
      return;
    act(j, async () => {
      const r = await mikrotikCall(`${BASE}/${j.id}/reset-hostkey`, 'POST', {});
      setNotice(
        r.ok && r.json?.success
          ? { tone: 'ok', text: `Nouvelle clé de « ${j.name} » acceptée et mémorisée.` }
          : { tone: 'crit', text: r.json?.status ? jumpFailureMessage(r.json) : r.json?.error || 'Échec.' }
      );
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[960px] max-w-full max-h-[92vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-3 flex-none flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Network size={18} strokeWidth={1.75} className="text-blue-400" />
            <h2 className="text-base font-bold text-fg">Serveurs de rebond SSH</h2>
          </div>
          <button
            onClick={onClose}
            className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
            aria-label="Fermer"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          <p className="text-[11px] text-muted-2 mb-3 leading-relaxed">
            Un serveur de rebond (OpenSSH) sert de passage vers les routeurs qui ne sont joignables que depuis un réseau
            de gestion. Son compte doit autoriser la redirection de ports (<code className="font-mono">AllowTcpForwarding</code>).
          </p>

          {canEdit && !form && (
            <button
              onClick={() => setForm('new')}
              className="mb-3 bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-xs font-semibold hover:bg-blue-600 inline-flex items-center gap-1.5"
            >
              <Plus size={14} strokeWidth={2} /> Ajouter un serveur de rebond
            </button>
          )}

          {notice && (
            <div className="mb-3">
              <Alert tone={notice.tone}>
                {notice.text}
                {notice.mismatch && canEdit && (
                  <>
                    {' '}
                    <button className={btnCls} onClick={() => resetKey(notice.mismatch)}>
                      Accepter la nouvelle clé
                    </button>
                  </>
                )}
              </Alert>
            </div>
          )}
          {loadError && <Alert>{loadError}</Alert>}

          <div className="border border-rule rounded-xl overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={thCls}>Nom</th>
                  <th className={thCls}>Hôte : port</th>
                  <th className={thCls}>Utilisateur</th>
                  <th className={thCls}>Routeurs</th>
                  <th className={thCls}>État</th>
                  <th className={thCls}>Empreinte SSH</th>
                  <th className={thCls}></th>
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-muted-2 text-xs">
                      Chargement…
                    </td>
                  </tr>
                )}
                {rows && rows.length === 0 && !loadError && (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-muted-2 text-xs">
                      Aucun serveur de rebond configuré.
                    </td>
                  </tr>
                )}
                {(rows || []).map((j) => {
                  const down = j.lastCheckStatus && j.lastCheckStatus !== 'ok';
                  const mismatch = j.lastCheckStatus === 'hostkey-mismatch';
                  return (
                    <tr key={j.id}>
                      <td className={tdCls}>
                        <span className="font-semibold text-fg">{j.name}</span>
                        {j.isDefault && (
                          <span className="ml-2 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-500/15 text-blue-400 border border-blue-500/30">
                            <Star size={10} strokeWidth={2} /> Par défaut
                          </span>
                        )}
                      </td>
                      <td className={`${tdCls} font-mono text-[11.5px]`}>
                        {j.host}:{j.port}
                      </td>
                      <td className={tdCls}>{j.username}</td>
                      <td className={tdCls}>{j.routerCount}</td>
                      <td className={tdCls}>
                        {!j.lastCheckStatus ? (
                          <span className="text-muted-2">Pas encore vérifié</span>
                        ) : down ? (
                          <span className="text-crit" title={j.lastCheckError || undefined}>
                            {mismatch ? 'Clé SSH changée' : 'Injoignable'}
                            {j.downSince ? ' depuis ' + fmtRelativeFromNow(j.downSince) : ''}
                            {j.lastCheckError && (
                              <div className="text-[10px] text-muted-2 max-w-[200px] break-words">{j.lastCheckError}</div>
                            )}
                          </span>
                        ) : (
                          <span className="text-ok" title={j.lastCheckAt ? fmtDateTime(j.lastCheckAt) : undefined}>
                            Joignable
                          </span>
                        )}
                      </td>
                      <td className={`${tdCls} font-mono text-[10.5px] break-all max-w-[200px]`}>
                        {j.hostKeyFingerprint ? (j.hostKeyAlgorithm ? j.hostKeyAlgorithm + ' ' : '') + j.hostKeyFingerprint : '—'}
                      </td>
                      <td className={`${tdCls} whitespace-nowrap`}>
                        <div className="flex flex-wrap gap-1 justify-end">
                          <button className={btnCls} disabled={busy === j.id} onClick={() => test(j)}>
                            {busy === j.id && <Loader2 size={11} className="animate-spin" />} Tester
                          </button>
                          {canEdit && (
                            <>
                              {mismatch && (
                                <button className={btnCls} disabled={busy === j.id} onClick={() => resetKey(j)}>
                                  Accepter la nouvelle clé
                                </button>
                              )}
                              {!j.isDefault && (
                                <button className={btnCls} disabled={busy === j.id} onClick={() => setDefault(j)}>
                                  Définir par défaut
                                </button>
                              )}
                              <button className={btnCls} disabled={busy === j.id} onClick={() => setForm(j)} aria-label="Modifier">
                                <Pencil size={11} strokeWidth={1.75} />
                              </button>
                              <button className={btnCls} disabled={busy === j.id} onClick={() => remove(j)} aria-label="Supprimer">
                                <Trash2 size={11} strokeWidth={1.75} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {form && (
            <JumpForm
              key={form === 'new' ? 'new' : form.id}
              jump={form === 'new' ? null : form}
              onClose={(saved) => {
                setForm(null);
                if (saved) {
                  setNotice(null);
                  load();
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
